import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getScadenze, createScadenza, updateScadenza, deleteScadenza,
  exportScadenzeXlsx, importScadenzeXlsx,
  Mezzo, Scadenza,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIPOLOGIE = ['revisione', 'bollo', 'assicurazione', 'tagliando', 'patente', 'cqc', 'altro'];

const STATO_CONFIG = {
  ok:          { bg: '#dcfce7', color: '#15803d', label: '✅ OK' },
  in_scadenza: { bg: '#fef9c3', color: '#854d0e', label: '⚠️ In scadenza' },
  scaduta:     { bg: '#fee2e2', color: '#991b1b', label: '❌ Scaduta' },
};

const TIPO_ICONS: Record<string, string> = {
  revisione: '🔍', bollo: '📋', assicurazione: '🛡️',
  tagliando: '🔩', patente: '📄', cqc: '📜', altro: '📌',
};

const EMPTY: Partial<Scadenza> = {
  tipologia: 'revisione',
  data_scadenza: '',
  notifica_giorni_prima: 30,
};

export default function ScadenzeFleet() {
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [scadenze, setScadenze] = useState<Scadenza[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMezzo, setFilterMezzo] = useState<number | ''>('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Scadenza | null>(null);
  const [form, setForm] = useState<Partial<Scadenza>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (filterTipo) params.tipologia = filterTipo;
      if (filterStato) params.stato = filterStato;
      const res = await exportScadenzeXlsx(params);
      downloadBlob(res.data as Blob, 'fleet_scadenze.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importScadenzeXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} create, ${updated} aggiornate${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => {
    getMezzi(true).then(r => setMezzi(r.data));
    load();
  }, []);

  useEffect(() => { load(); }, [filterMezzo, filterTipo, filterStato]);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (filterTipo)  params.tipologia = filterTipo;
      if (filterStato) params.stato     = filterStato;
      const res = await getScadenze(params);
      setScadenze(res.data);
    } catch {
      setError('Errore caricamento scadenze');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, mezzo_id: filterMezzo || undefined });
    setError('');
    setShowForm(true);
  };

  const openEdit = (s: Scadenza) => {
    setEditing(s);
    setForm({ ...s });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.mezzo_id)      { setError('Seleziona un veicolo'); return; }
    if (!form.data_scadenza) { setError('Data scadenza obbligatoria'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateScadenza(editing.id, form);
      } else {
        await createScadenza(form);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminare questa scadenza?')) return;
    try {
      await deleteScadenza(id);
      load();
    } catch {
      setError('Errore eliminazione');
    }
  };

  const set = (patch: Partial<Scadenza>) => setForm(f => ({ ...f, ...patch }));

  const nScadute = scadenze.filter(s => s.stato === 'scaduta').length;
  const nUrgenti = scadenze.filter(s => s.stato === 'in_scadenza').length;

  return (
    <div className="fleet-section">

      {(nScadute > 0 || nUrgenti > 0) && (
        <div className="fleet-alert-banner">
          {nScadute > 0 && <span className="alert-item scaduta">❌ {nScadute} scadenza/e scaduta/e</span>}
          {nUrgenti > 0 && <span className="alert-item urgente">⚠️ {nUrgenti} in scadenza entro 30 giorni</span>}
        </div>
      )}

      <div className="fleet-toolbar">
        <select className="fleet-filter" value={filterMezzo} onChange={e => setFilterMezzo(parseInt(e.target.value) || '')}>
          <option value="">Tutti i veicoli</option>
          {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
        </select>
        <select className="fleet-filter" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t}</option>)}
        </select>
        <select className="fleet-filter" value={filterStato} onChange={e => setFilterStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="ok">✅ OK</option>
          <option value="in_scadenza">⚠️ In scadenza</option>
          <option value="scaduta">❌ Scaduta</option>
        </select>
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Nuova Scadenza</button>
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : scadenze.length === 0 ? (
        <div className="fleet-stub">Nessuna scadenza trovata</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Veicolo</th>
              <th>Tipo</th>
              <th>Data scadenza</th>
              <th>Giorni</th>
              <th>Stato</th>
              <th>Data pagamento</th>
              <th>Costo</th>
              <th>Fornitore</th>
              <th>Note</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {scadenze.map(s => {
              const cfg = STATO_CONFIG[s.stato || 'ok'];
              return (
                <tr key={s.id}>
                  <td>
                    <strong>{s.mezzo_targa}</strong>
                    {s.mezzo_modello && <><br /><span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{s.mezzo_modello}</span></>}
                  </td>
                  <td>{TIPO_ICONS[s.tipologia]} {s.tipologia}</td>
                  <td><strong>{new Date(s.data_scadenza).toLocaleDateString('it-IT')}</strong></td>
                  <td style={{ color: (s.giorni_alla_scadenza ?? 0) < 0 ? '#ef4444' : '#374151' }}>
                    {s.giorni_alla_scadenza !== undefined
                      ? s.giorni_alla_scadenza < 0
                        ? `${Math.abs(s.giorni_alla_scadenza)}gg fa`
                        : `${s.giorni_alla_scadenza} gg`
                      : '—'}
                  </td>
                  <td>
                    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 8px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600 }}>
                      {cfg.label}
                    </span>
                  </td>
                  <td>{s.data_pagamento ? new Date(s.data_pagamento).toLocaleDateString('it-IT') : '—'}</td>
                  <td>{s.costo ? `€ ${Number(s.costo).toFixed(2)}` : '—'}</td>
                  <td>{s.fornitore_nome || '—'}</td>
                  <td style={{ maxWidth: 100, fontSize: '0.8rem', color: '#6b7280' }}>{s.descrizione || '—'}</td>
                  <td>
                    <button className="btn-icon" onClick={() => openEdit(s)}>✏️</button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(s.id)}>🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Scadenza' : 'Nuova Scadenza'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}
              <div className="form-grid-3">
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Veicolo *</label>
                  <select value={form.mezzo_id || ''} onChange={e => set({ mezzo_id: parseInt(e.target.value) || undefined })}>
                    <option value="">— Seleziona veicolo —</option>
                    {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipologia *</label>
                  <select value={form.tipologia || ''} onChange={e => set({ tipologia: e.target.value })}>
                    {TIPOLOGIE.map(t => <option key={t} value={t}>{TIPO_ICONS[t]} {t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Data scadenza *</label>
                  <input type="date" value={form.data_scadenza || ''} onChange={e => set({ data_scadenza: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data pagamento</label>
                  <input type="date" value={form.data_pagamento || ''} onChange={e => set({ data_pagamento: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Costo (€)</label>
                  <input type="number" step="0.01" value={form.costo || ''} onChange={e => set({ costo: parseFloat(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>Fornitore / Ente</label>
                  <input value={form.fornitore_nome || ''} onChange={e => set({ fornitore_nome: e.target.value })} placeholder="ACI, Motorizzazione..." />
                </div>
                <div className="form-group">
                  <label>Notifica (giorni prima)</label>
                  <input type="number" value={form.notifica_giorni_prima ?? 30} onChange={e => set({ notifica_giorni_prima: parseInt(e.target.value) || 30 })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Note / Descrizione</label>
                  <textarea rows={2} value={form.descrizione || ''} onChange={e => set({ descrizione: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .fleet-alert-banner { display:flex; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
        .alert-item { padding:8px 14px; border-radius:8px; font-size:0.875rem; font-weight:600; }
        .alert-item.scaduta { background:#fee2e2; color:#991b1b; }
        .alert-item.urgente { background:#fef9c3; color:#854d0e; }
      `}</style>
    </div>
  );
}
